import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'features/rootReducer';
import { getInitialValidation, getInitialMainBannerFormData } from 'modules/mainBannerForm';
import { CallbackOptions } from 'types/common/callback';
import {
  LinkFormData,
  MainBannerFormData,
  MainBannerFormValidation,
  TextFormData,
} from 'types/marketplace/operation/mainBanner/mainBannerForm';

export interface MainBannerFormState {
  id: string;
  formData: MainBannerFormData;
  initialFormData: MainBannerFormData;
  validation: MainBannerFormValidation;
  isLoading: boolean;
}

const initialState: MainBannerFormState = {
  id: '',
  formData: getInitialMainBannerFormData(),
  initialFormData: getInitialMainBannerFormData(),
  validation: getInitialValidation(),
  isLoading: false,
};

export const mainBannerFormSlice = createSlice({
  name: 'mainBannerForm',
  initialState,
  reducers: {
    setBannerName: (state: MainBannerFormState, { payload }: PayloadAction<string>) => {
      state.formData.bannerName = payload;
    },
    setImage: (state: MainBannerFormState, { payload }: PayloadAction<File | null>) => {
      state.formData.image = payload;
    },
    setText: (state: MainBannerFormState, { payload }: PayloadAction<TextFormData>) => {
      state.formData.text = payload;
    },
    setTextColor: (state: MainBannerFormState, { payload }: PayloadAction<string>) => {
      state.formData.textColor = payload;
    },
    setLink: (state: MainBannerFormState, { payload }: PayloadAction<LinkFormData>) => {
      state.formData.link = payload;
    },
    setValidation: (
      state: MainBannerFormState,
      { payload }: PayloadAction<MainBannerFormValidation>,
    ) => {
      state.validation = payload;
    },
    setFormData: (state: MainBannerFormState, { payload }: PayloadAction<MainBannerFormData>) => {
      state.formData = payload;
    },
    reset: () => initialState,
    setIsLoading: (state: MainBannerFormState, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
    createBanner: (_, _action: PayloadAction<CallbackOptions>) => {},
    updateBanner: (_, _action: PayloadAction<{ id: string } & CallbackOptions>) => {},
    loadFormData: (_, _action: PayloadAction<string>) => {},
    setId: (state: MainBannerFormState, { payload: id }: PayloadAction<string>) => {
      state.id = id;
    },
    setInitialFormData: (
      state: MainBannerFormState,
      { payload }: PayloadAction<MainBannerFormData>,
    ) => {
      state.initialFormData = payload;
    },
  },
});

export const mainBannerFormActions = mainBannerFormSlice.actions;
export const mainBannerFormReducer = mainBannerFormSlice.reducer;

export const selectMainBannerFormState = (rootState: RootState): MainBannerFormState =>
  rootState.marketplace.mainBannerForm;
export const selectFormData = (rootState: RootState): MainBannerFormData =>
  rootState.marketplace.mainBannerForm.formData;
export const selectValidation = (rootState: RootState): MainBannerFormValidation =>
  rootState.marketplace.mainBannerForm.validation;
